<template>
  <figure
    v-intersect
    class="product"
    @intersect:enter="onShowProductCard"
  >
    <!-- PREVIEW -->
    <div v-if="product.category.ar_d && (ageRestrictions || ageRestrictions === null)" class="product__preview">
      <ProductCardAgeRestrictions />
    </div>

    <a
      v-else
      class="product__preview"
      :href="link"
      @click.prevent="handleLinkClick(link)"
    >

      <BaseImage
        class="product__preview-img"
        width="120"
        height="120"
        size="300x300"
        :name="product.images[0]"
        :alt="product.name"
      />
    </a>

    <figcaption>
      <!-- PRODUCT TITLE -->
      <a
        class="product__title"
        :href="link"
        :title="product.name"
        @click.prevent="handleLinkClick(link)"
      >
        {{ product.name }}
      </a>

      <!-- PRICE -->
      <strong v-if="product.offer_main" class="product__price">
        <template
          v-if="product.offer_main.delivery.uuid === freeProductDeliveryUiid"
        >{{ $t('product.free') }}</template>
        <template v-else>
          {{ getFormattedPrice(product.offer_main.price) }}
          <span class="icon-hryvnia" />
        </template>
      </strong>

      <!-- PRICE -->
      <div v-else class="product__not-available">
        {{ $t('offers.not_available') }}
      </div>
    </figcaption>
  </figure>
</template>

<script lang="ts" setup>
import { FREE_PRODUCT_DELIVERY_UIID } from '@/constants/common'
import type { Product, ProductCatalog } from '~/types/product'

type Props = {
  product: Product | ProductCatalog
  isOffer?: boolean
  block?: string
}

const props = withDefaults(defineProps<Props>(), {
  isOffer: false,
  block: ''
})
const localePath = useLocalePath()
const authUserStore = useAuthUserStore()
const commonMetricsStore = useCommonMetricsStore()

const freeProductDeliveryUiid = FREE_PRODUCT_DELIVERY_UIID

const ageRestrictions = computed(() => {
  return authUserStore.ageRestrictions
})

const link = computed(() => {
  let ulr: string = `/product/${props.product.slug}/${props.product.uuid}`

  if (props.isOffer && props.product.offer_main) {
    ulr += `/${props.product.offer_main.code}`
  }

  return localePath(ulr)
})

const offerCode = computed(() => {
  return props.product?.offer_main !== null ? props.product.offer_main.code : ''
})

const onShowProductCard = () => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[product-card-small]',
    event_type: 'view',
    event_content: {
      code: offerCode.value,
      block: props.block
    }
  })
}

const handleLinkClick = (url: string) => {
  commonMetricsStore.addMetricEvent({
    element: 'ms[product-card-small]',
    event_type: 'click',
    event_content: {
      code: offerCode.value,
      block: props.block
    }
  })

  navigateTo(url)
}
</script>

<style lang="scss" scoped>
.product {
  background: #fff;
  overflow: hidden;
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  white-space: initial;
  padding: 1.2rem;
  transition: $transition-time;

  &:hover {
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  }

  & figcaption {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
  }

  &__preview {
    flex-shrink: 0;
    display: block;
    height: 12rem;
    margin-bottom: 1rem;
    @media only screen and (min-width: 740px) {
      height: 14.2rem;
    }
  }
  &__preview-img {
    object-fit: contain;
    @media only screen and (min-width: 740px) {
      width: 100%;
      height: 100%;
    }
  }

  &__subtitle {
    margin-top: 0.3rem;
  }

  &__title {
    font-size: 1.4rem;
    line-height: 1.8rem;
    color: $font-color;
    display: -webkit-box;
    word-break: break-word;
    text-decoration: none;
    overflow: hidden;
    height: 3.6rem;
  }

  &__price {
    padding-top: 10px;
    font-weight: 600;
    font-size: 1.8rem;
    display: block;
    line-height: 2.1rem;
  }

  &__not-available {
    padding-top: 10px;
    display: block;
    color: #737373;
    font-size: 1.2rem;
    line-height: 1.8rem;
  }
}
</style>
